import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNotification } from "../common/NotificationContext";
import {
  collection,
  query,
  getDocs,
  doc,
  deleteDoc,
  writeBatch,
  getDoc,
  updateDoc,
  getCountFromServer,
  limit,
  where,
  startAfter,
  orderBy,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faTrash,
  faSearch,
  faEnvelope,
  faDownload,
  faChevronDown,
  faSortUp,
  faSortDown,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import Papa from "papaparse";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

// Konstanta untuk pagination
const PAGE_SIZE = 50;

// Komponen untuk baris skeleton saat loading
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(9) // Diperbarui menjadi 9 karena kolom grupPengkot ditambahkan
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

export const Participants = ({ user }) => {
  const { showNotification, showConfirm } = useNotification();
  const [state, setState] = useState({
    participants: [],
    filteredParticipants: [],
    searchQuery: "",
    selectedParticipants: [],
    loading: true,
    loadingMore: false,
    lastVisible: null,
    hasMore: true,
    totalParticipants: 0,
    totalFiltered: 0,
    sortKategoriDirection: null,
    allParticipants: [],
    emailTemplateId: 3, // Default template ID untuk Akomodasi Grand Aston
  });
  const [viewParticipant, setViewParticipant] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvSuccessCount, setCsvSuccessCount] = useState(0);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [skipNameValidation, setSkipNameValidation] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const db = getFirestore();
  const navigate = useNavigate();
  const isMounted = useRef(true);

  // Cleanup effect
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Validasi format BIB Number
  const validateBibNumber = (bibNumber) => {
    if (bibNumber === "" || bibNumber === null || bibNumber === undefined) {
      return true;
    }
    return /^\d{1,4}$/.test(bibNumber);
  };

  // Normalisasi string
  const normalizeString = (str) => {
    return str ? str.trim().replace(/\s+/g, " ").toLowerCase() : "";
  };

  // Handler untuk unggah file CSV
  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFileName(file.name);
    setCsvErrors([]);
    setCsvSuccessCount(0);
    setCsvData([]);
    setSkipNameValidation(false);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const data = result.data;
        if (!data.length) {
          showNotification("error", "CSV kosong atau tidak valid.");
          return;
        }

        const requiredHeaders = [
          "Order ID",
          "Nama Depan",
          "Nama Belakang",
          "BIB Number",
        ];
        const headers = Object.keys(data[0]);
        const missingHeaders = requiredHeaders.filter(
          (header) => !headers.includes(header)
        );
        if (missingHeaders.length > 0) {
          showNotification(
            "error",
            `Header CSV tidak lengkap. Harus menyertakan: ${missingHeaders.join(
              ", "
            )}`
          );
          return;
        }

        setCsvData(data);
        setIsCsvModalOpen(true);
      },
      error: (err) => {
        console.error("[ERROR] Error parsing CSV:", err);
        showNotification(
          "error",
          "Gagal memproses file CSV. Pastikan formatnya benar."
        );
      },
    });
  };

  const handleProcessCsv = async () => {
    if (!csvData.length) {
      showNotification("error", "Tidak ada data CSV untuk diproses.");
      return;
    }

    setIsProcessingCsv(true);
    setCsvErrors([]);
    setCsvSuccessCount(0);
    const newErrors = [];
    const updates = [];

    for (const row of csvData) {
      const {
        "Order ID": orderId,
        "Nama Depan": namaDepan,
        "Nama Belakang": namaBelakang,
        "BIB Number": bibNumber,
      } = row;

      if (!orderId || typeof orderId !== "string") {
        newErrors.push(
          `Baris ${csvData.indexOf(row) + 2}: Order ID tidak valid.`
        );
        continue;
      }
      if (!namaDepan || typeof namaDepan !== "string") {
        newErrors.push(
          `Baris ${csvData.indexOf(row) + 2}: Nama Depan tidak valid.`
        );
        continue;
      }
      if (!validateBibNumber(bibNumber)) {
        newErrors.push(
          `Baris ${
            csvData.indexOf(row) + 2
          }: BIB Number harus 1-4 digit angka atau kosong.`
        );
        continue;
      }

      const participantRef = doc(db, "pendaftaranUltra24", orderId);
      const participantDoc = await getDoc(participantRef);
      if (!participantDoc.exists()) {
        newErrors.push(
          `Baris ${
            csvData.indexOf(row) + 2
          }: Order ID ${orderId} tidak ditemukan.`
        );
        continue;
      }

      const participantData = participantDoc.data();
      let updateData = null;

      const isGroup =
        participantData.participants &&
        Array.isArray(participantData.participants) &&
        participantData.participants.length > 0;

      if (!isGroup) {
        let nameMatch = true;
        if (!skipNameValidation) {
          const namaDepanDb = normalizeString(
            participantData.namaLengkap
              ? participantData.namaLengkap.split(" ")[0]
              : ""
          );
          const namaDepanCsvNorm = normalizeString(namaDepan);
          if (namaDepanDb !== namaDepanCsvNorm) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Nama Depan "${namaDepan}" tidak cocok dengan "${
                participantData.namaLengkap.split(" ")[0]
              }" untuk Order ID ${orderId} (individu).`
            );
            nameMatch = false;
          }
        }

        if (skipNameValidation || nameMatch) {
          updateData = {
            orderId,
            bibNumber: bibNumber || "",
            isGroup: false,
            ref: participantRef,
            currentBibNumber: participantData.bibNumber || "",
          };
        }
      } else {
        let participantFound = false;
        const namaDepanCsvNorm = normalizeString(namaDepan);

        const namaLengkapDb = normalizeString(
          participantData.namaLengkap || ""
        );
        const namaDepanDbFromNamaLengkap = namaLengkapDb.split(" ")[0] || "";
        if (
          !skipNameValidation &&
          namaDepanCsvNorm === namaDepanDbFromNamaLengkap
        ) {
          updateData = {
            orderId,
            bibNumber: bibNumber || "",
            isGroup: false,
            ref: participantRef,
            currentBibNumber: participantData.bibNumber || "",
          };
          participantFound = true;
        } else {
          const participantIndex = participantData.participants.findIndex(
            (p) => normalizeString(p.namaDepan || "") === namaDepanCsvNorm
          );
          if (participantIndex !== -1) {
            if (!skipNameValidation) {
              const namaDepanParticipant = normalizeString(
                participantData.participants[participantIndex].namaDepan || ""
              );
              if (namaDepanParticipant !== namaDepanCsvNorm) {
                newErrors.push(
                  `Baris ${
                    csvData.indexOf(row) + 2
                  }: Nama Depan "${namaDepan}" tidak cocok dengan "${
                    participantData.participants[participantIndex].namaDepan
                  }" di grup ${orderId}.`
                );
              } else {
                updateData = {
                  orderId,
                  bibNumber: bibNumber || "",
                  isGroup: true,
                  ref: participantRef,
                  participantIndex,
                  currentBibNumber:
                    participantData.participants[participantIndex].bibNumber ||
                    "",
                };
                participantFound = true;
              }
            } else {
              updateData = {
                orderId,
                bibNumber: bibNumber || "",
                isGroup: true,
                ref: participantRef,
                participantIndex,
                currentBibNumber:
                  participantData.participants[participantIndex].bibNumber ||
                  "",
              };
              participantFound = true;
            }
          }
        }

        if (!participantFound) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Tidak ditemukan peserta dengan Nama Depan "${namaDepan}" di grup ${orderId}.`
          );
        }
      }

      if (updateData) {
        updates.push(updateData);
      }
    }

    const bibNumberMap = new Map();
    for (const update of updates) {
      const { bibNumber, orderId } = update;
      if (bibNumber) {
        if (bibNumberMap.has(bibNumber)) {
          newErrors.push(
            `Baris untuk Order ID ${orderId}: BIB Number ${bibNumber} sudah digunakan di baris lain dalam CSV untuk Order ID ${bibNumberMap.get(
              bibNumber
            )}.`
          );
        } else {
          bibNumberMap.set(bibNumber, orderId);
        }
      }
    }

    if (newErrors.length === 0 && updates.length > 0) {
      try {
        const existingBibNumbers = new Map();
        const allDocs = await getDocs(collection(db, "pendaftaranUltra24"));
        allDocs.forEach((doc) => {
          const data = doc.data();
          if (data.bibNumber) {
            existingBibNumbers.set(data.bibNumber, {
              orderId: doc.id,
              isGroup: false,
            });
          }
          if (data.participants && Array.isArray(data.participants)) {
            data.participants.forEach((p, index) => {
              if (p.bibNumber) {
                existingBibNumbers.set(p.bibNumber, {
                  orderId: doc.id,
                  isGroup: true,
                  participantIndex: index,
                });
              }
            });
          }
        });

        const csvBibNumbers = new Set(
          updates.map((u) => u.bibNumber).filter(Boolean)
        );
        const csvOrderIds = new Set(updates.map((u) => u.orderId));

        for (const update of updates) {
          const {
            bibNumber,
            orderId,
            isGroup,
            participantIndex,
            currentBibNumber,
          } = update;
          if (bibNumber && existingBibNumbers.has(bibNumber)) {
            const existing = existingBibNumbers.get(bibNumber);
            if (
              existing.orderId === orderId &&
              isGroup === existing.isGroup &&
              (!isGroup || participantIndex === existing.participantIndex)
            ) {
              continue;
            }
            if (
              csvBibNumbers.has(bibNumber) &&
              csvOrderIds.has(existing.orderId)
            ) {
              continue;
            }
            newErrors.push(
              `Baris untuk Order ID ${orderId}: BIB Number ${bibNumber} sudah digunakan di ${
                existing.isGroup
                  ? `grup ${existing.orderId}, peserta index ${existing.participantIndex}`
                  : `dokumen ${existing.orderId}`
              }.`
            );
          }
        }

        if (newErrors.length === 0) {
          const bibToOrderId = new Map(
            updates.map((u) => [u.bibNumber, u.orderId])
          );
          for (const update of updates) {
            const { bibNumber, orderId, currentBibNumber } = update;
            if (currentBibNumber && bibToOrderId.has(currentBibNumber)) {
              const targetOrderId = bibToOrderId.get(currentBibNumber);
              if (
                targetOrderId !== orderId &&
                !csvOrderIds.has(targetOrderId)
              ) {
                newErrors.push(
                  `Baris untuk Order ID ${orderId}: Penukaran BIB Number ${bibNumber} dengan ${currentBibNumber} tidak valid karena ${currentBibNumber} digunakan di luar CSV.`
                );
              }
            }
          }
        }
      } catch (err) {
        console.error("[ERROR] Error checking bibNumber uniqueness:", err);
        newErrors.push("Gagal memeriksa keunikan BIB Number di database.");
      }
    }

    if (newErrors.length === 0 && updates.length > 0) {
      const batch = writeBatch(db);
      for (const update of updates) {
        const { ref, bibNumber, isGroup, participantIndex } = update;
        if (isGroup) {
          const docSnapshot = await getDoc(ref);
          const participants = docSnapshot.data().participants;
          participants[participantIndex] = {
            ...participants[participantIndex],
            bibNumber: bibNumber || "",
          };
          batch.update(ref, {
            participants,
            updatedAt: new Date(),
          });
        } else {
          batch.update(ref, {
            bibNumber: bibNumber || "",
            updatedAt: new Date(),
          });
        }
      }

      try {
        await batch.commit();
        setCsvSuccessCount(updates.length);
        setState((prev) => {
          let updatedParticipants = [...prev.participants];
          let updatedFilteredParticipants = [...prev.filteredParticipants];
          let updatedAllParticipants = [...prev.allParticipants];

          updates.forEach(
            ({ orderId, bibNumber, isGroup, participantIndex }) => {
              const updateParticipant = (p) => {
                if (p.id === orderId) {
                  if (isGroup) {
                    const participants = [...p.participants];
                    participants[participantIndex] = {
                      ...participants[participantIndex],
                      bibNumber: bibNumber || "",
                    };
                    return { ...p, participants };
                  }
                  return { ...p, bibNumber: bibNumber || "" };
                }
                return p;
              };

              updatedParticipants = updatedParticipants.map(updateParticipant);
              updatedFilteredParticipants =
                updatedFilteredParticipants.map(updateParticipant);
              updatedAllParticipants =
                updatedAllParticipants.map(updateParticipant);
            }
          );

          return {
            ...prev,
            participants: updatedParticipants,
            filteredParticipants: updatedFilteredParticipants,
            allParticipants: updatedAllParticipants,
          };
        });
        showNotification(
          "success",
          `Berhasil memperbarui ${updates.length} BIB Number.`
        );
        setIsCsvModalOpen(false);
        setCsvData([]);
        setCsvFileName("");
      } catch (err) {
        console.error("[ERROR] Error committing batch:", err);
        newErrors.push("Gagal memperbarui BIB Number di database.");
      }
    }

    setCsvErrors(newErrors);
    setIsProcessingCsv(false);

    if (newErrors.length > 0) {
      showNotification(
        "error",
        `Ada ${newErrors.length} kesalahan. Lihat detail di modal.`
      );
    }
  };

  // Fetch peserta dengan pagination
  const loadParticipants = useCallback(
    async (lastDoc = null, sortDirection = state.sortKategoriDirection) => {
      if (!isMounted.current) return;

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        filteredParticipants: state.searchQuery
          ? prev.filteredParticipants
          : [],
      }));

      try {
        const participantsCollection = collection(db, "pendaftaranUltra24");
        const constraints = [orderBy("namaLengkap")];
        if (sortDirection) {
          constraints.unshift(orderBy("kategori", sortDirection));
        }
        const baseQuery = lastDoc
          ? query(
              participantsCollection,
              ...constraints,
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(participantsCollection, ...constraints, limit(PAGE_SIZE));

        const participantsSnapshot = await getDocs(baseQuery);
        const participantsList = participantsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          sentTemplateIds: doc.data().sentTemplateIds || [],
          grupPengkot: doc.data().grupPengkot || "-", // Pastikan grupPengkot selalu ada
        }));

        if (!isMounted.current) return;

        setState((prev) => {
          const updatedParticipants = lastDoc
            ? [...prev.participants, ...participantsList]
            : participantsList;
          return {
            ...prev,
            participants: updatedParticipants,
            filteredParticipants: state.searchQuery
              ? prev.filteredParticipants
              : updatedParticipants,
            lastVisible:
              participantsSnapshot.docs[participantsSnapshot.docs.length - 1] ||
              null,
            loading: false,
            loadingMore: false,
            hasMore: participantsSnapshot.docs.length === PAGE_SIZE,
            totalFiltered: state.searchQuery
              ? prev.totalFiltered
              : prev.totalParticipants,
          };
        });
      } catch (err) {
        console.error("[ERROR] Error fetching participants:", err);
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
            loadingMore: false,
            error: "Gagal memuat data peserta. Silakan coba lagi.",
          }));
          showNotification(
            "error",
            "Gagal memuat data peserta. Silakan coba lagi."
          );
        }
      }
    },
    [state.searchQuery, state.sortKategoriDirection, showNotification]
  );

  // Fetch total jumlah peserta using aggregations
  const fetchTotalParticipants = useCallback(async () => {
    try {
      const participantsCollection = collection(db, "pendaftaranJOTR");
      const snapshot = await getCountFromServer(participantsCollection);
      if (isMounted.current) {
        const count = snapshot.data().count;
        setState((prev) => ({
          ...prev,
          totalParticipants: count,
          totalFiltered: state.searchQuery ? prev.totalFiltered : count,
        }));
      }
    } catch (err) {
      console.error("[ERROR] Error fetching total participants:", err);
    }
  }, [state.searchQuery]);

  // Fetch semua peserta untuk pencarian
  // REMOVED fetchAllParticipants to save costs.
  // Search is now handled differently or via server-side if needed.
  // For now, we'll just not fetch all participants on load.
  const fetchAllParticipants = useCallback(async () => {
    // This function is now a no-op or reduced to save costs.
    // If you need full search, implement server-side search instead.
  }, []);

  // Efek untuk load data awal
  useEffect(() => {
    loadParticipants();
    fetchTotalParticipants();
    fetchAllParticipants();
  }, [loadParticipants, fetchTotalParticipants, fetchAllParticipants]);

  // Handle pencarian
  useEffect(() => {
    if (state.searchQuery) {
      const filtered = (state.allParticipants || state.participants).filter(
        (p) =>
          [
            p.namaLengkap,
            p.email,
            p.orderId,
            p.kategori,
            p.transaction_status,
            p.bibNumber,
            p.sentTemplateIds?.join(", "),
            p.grupPengkot, // Tambahkan grupPengkot ke pencarian
          ]
            .filter(Boolean)
            .some((field) =>
              field
                .toString()
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase())
            )
      );
      setState((prev) => ({
        ...prev,
        filteredParticipants: filtered.slice(0, PAGE_SIZE),
        fullFilteredList: filtered,
        totalFiltered: filtered.length,
        hasMore: filtered.length > PAGE_SIZE,
        lastVisible: null,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        filteredParticipants: prev.participants,
        fullFilteredList: [],
        totalFiltered: prev.totalParticipants,
        hasMore: prev.participants.length < prev.totalParticipants,
      }));
    }
  }, [
    state.searchQuery,
    state.participants,
    state.allParticipants,
    state.totalParticipants,
  ]);

  // Load more peserta
  const loadMore = useCallback(() => {
    if (state.loading || state.loadingMore || !state.hasMore) return;

    if (state.searchQuery) {
      const currentIndex = Math.floor(
        state.filteredParticipants.length / PAGE_SIZE
      );
      const startIndex = currentIndex * PAGE_SIZE;
      const additionalParticipants = state.fullFilteredList.slice(
        startIndex,
        startIndex + PAGE_SIZE
      );
      if (!additionalParticipants.length) {
        setState((prev) => ({ ...prev, hasMore: false }));
        return;
      }
      setState((prev) => ({
        ...prev,
        filteredParticipants: [
          ...prev.filteredParticipants,
          ...additionalParticipants,
        ],
        hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
      }));
    } else {
      loadParticipants(state.lastVisible);
    }
  }, [
    state.loading,
    state.loadingMore,
    state.hasMore,
    state.searchQuery,
    state.filteredParticipants,
    state.fullFilteredList,
    state.lastVisible,
    loadParticipants,
  ]);

  // Toggle sortir kategori
  const handleKategoriSortToggle = useCallback(() => {
    const newDirection =
      state.sortKategoriDirection === "desc" ? "asc" : "desc";
    setState((prev) => ({
      ...prev,
      sortKategoriDirection: newDirection,
      lastVisible: null,
      hasMore: true,
      participants: [],
      filteredParticipants: [],
    }));
    loadParticipants(null, newDirection);
  }, [state.sortKategoriDirection, loadParticipants]);

  // Seleksi semua peserta
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setState((prev) => ({
        ...prev,
        selectedParticipants: prev.filteredParticipants
          .filter((p) => p.transaction_status === "settlement")
          .map((p) => p.id),
      }));
    } else {
      setState((prev) => ({ ...prev, selectedParticipants: [] }));
    }
  };

  // Seleksi satu peserta
  const handleSelectParticipant = (participantId) => {
    setState((prev) => ({
      ...prev,
      selectedParticipants: prev.selectedParticipants.includes(participantId)
        ? prev.selectedParticipants.filter((id) => id !== participantId)
        : [...prev.selectedParticipants, participantId],
    }));
  };

  // Ekspor ke CSV
  const handleExportCSV = () => {
    if (state.selectedParticipants.length === 0) {
      showNotification(
        "error",
        "Pilih setidaknya satu peserta untuk diekspor."
      );
      return;
    }

    const selectedData = state.allParticipants.filter(
      (p) =>
        state.selectedParticipants.includes(p.id) &&
        p.transaction_status === "settlement"
    );
    const headers = [
      "Order ID",
      "Nama",
      "BIB Number",
      "Kategori",
      "Status Transaksi",
      "Email",
      "Template Email Terkirim",
      "Gender",
      "Kota",
      "Alamat",
      "Provinsi",
      "Negara",
      "Telepon",
      "Nomor Identitas",
      "Tanggal Lahir",
      "Golongan Darah",
      "Kontak Darurat",
      "Nama Kontak Darurat",
      "Hubungan Kontak Darurat",
      "Bib Name",
      "Size Jersey",
      "Harga",
      "Jarak",
      "Grup Pengkot", // Tambahkan Grup Pengkot ke header
    ];

    const rows = selectedData.map((p) => [
      p.orderId || "-",
      p.namaLengkap || "-",
      p.bibNumber || "-",
      p.kategori || "-",
      p.transaction_status || "null",
      p.email || "-",
      p.sentTemplateIds?.length > 0 ? p.sentTemplateIds.join(", ") : "-",
      p.gender || "-",
      p.kota || "-",
      p.alamat || "-",
      p.provinsi || "-",
      p.negara || "-",
      p.telepon || "-",
      p.nomorIdentitas || "-",
      p.tanggalLahir || "-",
      p.golonganDarah || "-",
      p.kontakDarurat || "-",
      p.namaKontakDarurat || "-",
      p.hubunganKontakDarurat || "-",
      p.bibName || "-",
      p.sizeJersey || "-",
      p.harga || 0,
      p.distance || 0,
      p.grupPengkot || "-", // Tambahkan grupPengkot ke data
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((field) =>
            typeof field === "string" && field.includes(",")
              ? `"${field}"`
              : field
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `participants_export_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    showNotification("success", "Data peserta berhasil diekspor ke CSV.");
  };

  // Handler untuk aksi peserta
  const handleEdit = (participant) => {
    navigate("/participants/edit", { state: { participant } });
  };

  const handleView = (participant) => {
    setViewParticipant(participant);
  };

  const handleDelete = async (id) => {
    showConfirm(
      "Apakah Anda yakin ingin menghapus peserta ini?",
      async () => {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          const isUserAdmin =
            userDoc.exists() && userDoc.data().role === "admin";

          const participantRef = doc(db, "pendaftaranJOTR", id);
          const participantDoc = await getDoc(participantRef);

          if (!participantDoc.exists()) {
            showNotification("error", "Peserta tidak ditemukan.");
            return;
          }

          const participantData = participantDoc.data();
          const isOwner = participantData.uid === user.uid;

          if (!isUserAdmin && !isOwner) {
            showNotification(
              "error",
              "Anda tidak memiliki izin untuk menghapus peserta ini."
            );
            return;
          }

          await deleteDoc(participantRef);
          setState((prev) => ({
            ...prev,
            participants: prev.participants.filter((p) => p.id !== id),
            filteredParticipants: prev.filteredParticipants.filter(
              (p) => p.id !== id
            ),
            allParticipants: prev.allParticipants.filter((p) => p.id !== id),
            selectedParticipants: prev.selectedParticipants.filter(
              (selectedId) => selectedId !== id
            ),
            totalParticipants: prev.totalParticipants - 1,
            totalFiltered: prev.totalFiltered - 1,
          }));
          showNotification("success", "Peserta berhasil dihapus.");
        } catch (err) {
          console.error("[ERROR] Error deleting participant:", err);
          showNotification("error", "Gagal menghapus peserta: " + err.message);
        }
      },
      "Hapus",
      "Batal"
    );
  };

  // Validasi format email
  const isValidEmail = (email) => {
    if (!email || typeof email !== "string") {
      return false;
    }
    const trimmedEmail = email.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  };

  // Fungsi untuk delay (untuk menghindari rate limit)
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const sendBrevoTemplateViaBackend = async ({ to, name, templateId }) => {
    const token = await user?.getIdToken?.();
    if (!token) throw new Error("Not authenticated");

    const url = `${API_BASE_URL}/api/send-brevo-template`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        to,
        name,
        templateId,
        eventName: "ULTRA24",
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || "Failed to send email");
    }
    return data;
  };

  // Kirim email menggunakan Brevo API (satu per satu)
  const sendEmail = async (e) => {
    e.preventDefault();
    setIsSendingEmail(true);

    try {
      const templateId = state.emailTemplateId || 3;
      const eligibleParticipants = state.allParticipants.filter(
        (p) =>
          state.selectedParticipants.includes(p.id) &&
          p.transaction_status === "settlement" &&
          (!p.sentTemplateIds ||
            !p.sentTemplateIds.includes(templateId.toString()))
      );

      if (eligibleParticipants.length === 0) {
        showNotification(
          "error",
          `Tidak ada peserta yang dipilih dengan status settlement yang belum menerima template ID ${templateId}.`
        );
        setIsSendingEmail(false);
        return;
      }

      const recipients = eligibleParticipants
        .map((p) => ({
          id: p.id,
          email: p.email ? p.email.trim() : "",
          name: p.namaLengkap || "Peserta",
          isValid: isValidEmail(p.email),
        }))
        .filter((p) => p.isValid);

      if (recipients.length === 0) {
        showNotification(
          "error",
          `Tidak ada peserta yang dipilih dengan alamat email yang valid untuk template ID ${templateId}.`
        );
        setIsSendingEmail(false);
        return;
      }

      let totalSent = 0;
      const errors = [];

      for (const recipient of recipients) {
        try {
          await sendBrevoTemplateViaBackend({
            to: recipient.email,
            name: recipient.name,
            templateId,
          });

          // Update Firestore untuk recipient
          try {
            const participantRef = doc(db, "pendaftaranJOTR", recipient.id);
            const participantDoc = await getDoc(participantRef);
            const currentTemplateIds =
              participantDoc.data().sentTemplateIds || [];
            await updateDoc(participantRef, {
              emailSent: true,
              emailSentAt: new Date(),
              sentTemplateIds: [...currentTemplateIds, templateId.toString()],
            });

            setState((prev) => ({
              ...prev,
              participants: prev.participants.map((p) =>
                p.id === recipient.id
                  ? {
                      ...p,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(p.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : p
              ),
              filteredParticipants: prev.filteredParticipants.map((p) =>
                p.id === recipient.id
                  ? {
                      ...p,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(p.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : p
              ),
              allParticipants: prev.allParticipants.map((p) =>
                p.id === recipient.id
                  ? {
                      ...p,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(p.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : p
              ),
            }));

            totalSent += 1;
            showNotification(
              "info",
              `Berhasil mengirim email ke ${recipient.email}. (${totalSent}/${recipients.length})`
            );

            // Delay 100ms untuk menghindari rate limit
            await delay(100);
          } catch (err) {
            console.error(
              `[ERROR] Failed to update email status for ${recipient.id}:`,
              err
            );
            errors.push(
              `Gagal memperbarui status email untuk ${recipient.email}.`
            );
          }
        } catch (err) {
          console.error(
            `[ERROR] Error sending email to ${recipient.email}:`,
            err
          );
          errors.push(
            `Gagal mengirim email ke ${recipient.email}: ${
              err.message || "Terjadi kesalahan."
            }`
          );
        }
      }

      setIsEmailModalOpen(false);

      if (errors.length === 0) {
        showNotification(
          "success",
          `Email dengan template ID ${templateId} berhasil dikirim ke ${totalSent} penerima.`
        );
      } else {
        showNotification(
          "error",
          `Email dikirim ke ${totalSent} penerima, tetapi ada ${
            errors.length
          } kesalahan: ${errors.join("; ")}`
        );
      }
    } catch (err) {
      console.error("[ERROR] Failed to process email sending:", err);
      showNotification(
        "error",
        `Gagal mengirim email: ${err.message || "Terjadi kesalahan."}`
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handler pencarian
  const handleSearchChange = (e) => {
    setState((prev) => ({
      ...prev,
      searchQuery: e.target.value,
      hasMore: true,
    }));
  };

  // Teks untuk menampilkan jumlah peserta
  const rangeText = `${state.filteredParticipants.length} dari ${state.totalFiltered} Peserta`;

  return (
    <section className="min-h-screen py-20 bg-gray-100">
      <div className="container mx-auto px-4">
        {/* Kontrol Peserta */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="w-full max-w-md mx-auto md:ml-0">
            <InputField
              name="search"
              type="text"
              value={state.searchQuery}
              onChange={handleSearchChange}
              placeholder="Cari nama, email, order ID, kategori, BIB Number, grup pengkot, atau ID template..."
              aria-label="Cari peserta"
              startIcon={<FontAwesomeIcon icon={faSearch} className="text-gray-400" />}
            />
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => setIsEmailModalOpen(true)}
              disabled={isSendingEmail}
              variant="primary"
              className="flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Kirim Email
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={state.selectedParticipants.length === 0}
              variant="warning"
              className="flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export to CSV
            </Button>
            <Button
              variant="info"
              className="flex items-center gap-2 cursor-pointer relative"
              onClick={() => document.getElementById('csv-upload').click()}
              disabled={state.loading || isProcessingCsv}
            >
              <FontAwesomeIcon icon={faUpload} />
              Upload BIB Number
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
              />
            </Button>
          </div>
        </div>

        {/* Tabel Peserta */}
        <div className="bg-white rounded-xl border border-gray-300 overflow-x-auto mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      state.selectedParticipants.length ===
                        state.filteredParticipants.filter(
                          (p) => p.transaction_status === "settlement"
                        ).length && state.filteredParticipants.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-red-600 focus:ring-lime-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nama
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  BIB Number
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-red-600"
                  onClick={handleKategoriSortToggle}
                  aria-label="Urutkan berdasarkan kategori"
                >
                  <span className="flex items-center">
                    Kategori
                    {state.sortKategoriDirection && (
                      <FontAwesomeIcon
                        icon={
                          state.sortKategoriDirection === "asc"
                            ? faSortUp
                            : faSortDown
                        }
                        className="ml-1 text-gray-500"
                      />
                    )}
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status Transaksi
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template Email Terkirim
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grup Pengkot
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.loading ? (
                <SkeletonRow />
              ) : state.filteredParticipants.length === 0 ? (
                <tr>
                  <td
                    colSpan={9} // Diperbarui menjadi 9 karena kolom grupPengkot ditambahkan
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    {state.searchQuery
                      ? "Tidak ada peserta yang cocok dengan pencarian."
                      : "Belum ada peserta yang terdaftar."}
                  </td>
                </tr>
              ) : (
                state.filteredParticipants.map((p) => {
                  if (p.transaction_status !== "settlement") return null;
                  return (
                    <tr key={p.id}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={state.selectedParticipants.includes(p.id)}
                          onChange={() => handleSelectParticipant(p.id)}
                          className="h-4 w-4 text-red-600 focus:ring-lime-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {p.orderId || "-"}
                      </td>
                      <td
                        className={`px-4 py-4 whitespace-nowrap uppercase text-sm ${
                          p.gender === "Perempuan"
                            ? "text-red-600"
                            : "text-gray-600"
                        }`}
                      >
                        {p.namaLengkap ? p.namaLengkap.toUpperCase() : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                        {p.bibNumber ? (
                          <span className="bg-lime-100 text-lime-800 px-2 py-1 rounded text-xs">
                            {p.bibNumber}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {p.kategori || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {p.transaction_status || "null"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {p.sentTemplateIds?.length > 0
                          ? p.sentTemplateIds.join(", ")
                          : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {p.grupPengkot || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleView(p)}
                          className="text-red-600 hover:text-lime-900 mr-3"
                          disabled={state.loading}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          onClick={() => handleEdit(p)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                          disabled={state.loading}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="text-red-600 hover:text-red-900"
                          disabled={state.loading}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Load More dan Jumlah Peserta */}
        <div className="flex justify-end items-center px-4 py-2">
          <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
          <Button
            onClick={loadMore}
            disabled={state.loading || state.loadingMore || !state.hasMore}
            variant={state.loading || state.loadingMore || !state.hasMore ? "secondary" : "primary"}
            className="h-8 px-3 text-xs flex items-center gap-1"
          >
            <span>{state.hasMore ? "Lihat lebih" : "Akhir peserta"}</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
        </div>

        {/* Modal Detail Peserta */}
        {viewParticipant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-lg py-6 px-4 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Detail Peserta
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <p>
                  <strong>Order ID:</strong> {viewParticipant.orderId || "-"}
                </p>
                <p>
                  <strong>Nama:</strong> {viewParticipant.namaLengkap || "-"}
                </p>
                <p>
                  <strong>BIB Number:</strong>{" "}
                  {viewParticipant.bibNumber || "-"}
                </p>
                <p>
                  <strong>Email:</strong> {viewParticipant.email || "-"}
                </p>
                <p>
                  <strong>Kategori:</strong> {viewParticipant.kategori || "-"}
                </p>
                <p>
                  <strong>Status Transaksi:</strong>{" "}
                  {viewParticipant.transaction_status || "null"}
                </p>
                <p>
                  <strong>Template Email Terkirim:</strong>{" "}
                  {viewParticipant.sentTemplateIds?.length > 0
                    ? viewParticipant.sentTemplateIds.join(", ")
                    : "-"}
                </p>
                <p>
                  <strong>Harga:</strong>{" "}
                  {viewParticipant.harga
                    ? `Rp ${viewParticipant.harga.toLocaleString("id-ID")}`
                    : "-"}
                </p>
                <p>
                  <strong>Jarak:</strong>{" "}
                  {viewParticipant.distance
                    ? `${viewParticipant.distance} KM`
                    : "-"}
                </p>
                <p>
                  <strong>Alamat:</strong> {viewParticipant.alamat || "-"}
                </p>
                <p>
                  <strong>Kota:</strong> {viewParticipant.kota || "-"}
                </p>
                <p>
                  <strong>Provinsi:</strong> {viewParticipant.provinsi || "-"}
                </p>
                <p>
                  <strong>Negara:</strong> {viewParticipant.negara || "-"}
                </p>
                <p>
                  <strong>Telepon:</strong> {viewParticipant.telepon || "-"}
                </p>
                <p>
                  <strong>Nomor Identitas:</strong>{" "}
                  {viewParticipant.nomorIdentitas || "-"}
                </p>
                <p>
                  <strong>Tanggal Lahir:</strong>{" "}
                  {viewParticipant.tanggalLahir || "-"}
                </p>
                <p>
                  <strong>Gender:</strong> {viewParticipant.gender || "-"}
                </p>
                <p>
                  <strong>Golongan Darah:</strong>{" "}
                  {viewParticipant.golonganDarah || "-"}
                </p>
                <p>
                  <strong>Kontak Darurat:</strong>{" "}
                  {viewParticipant.kontakDarurat || "-"}
                </p>
                <p>
                  <strong>Nama Kontak Darurat:</strong>{" "}
                  {viewParticipant.namaKontakDarurat || "-"}
                </p>
                <p>
                  <strong>Hubungan Kontak Darurat:</strong>{" "}
                  {viewParticipant.hubunganKontakDarurat || "-"}
                </p>
                <p>
                  <strong>Bib Name:</strong> {viewParticipant.bibName || "-"}
                </p>
                <p>
                  <strong>Size Jersey:</strong>{" "}
                  {viewParticipant.sizeJersey || "-"}
                </p>
                <p>
                  <strong>Grup Pengkot:</strong>{" "}
                  {viewParticipant.grupPengkot || "-"}
                </p>
              </div>
              <div className="flex justify-center mt-6">
                <Button
                  onClick={() => setViewParticipant(null)}
                  variant="secondary"
                  className="w-full max-w-xs"
                >
                  Tutup
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Kirim Email */}
        {isEmailModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl py-6 px-4 max-w-lg w-full mx-4">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Kirim Email ke Peserta
              </h2>
              <form onSubmit={sendEmail}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Template ID (Brevo)
                  </label>
                  <InputField
                    type="number"
                    value={state.emailTemplateId}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        emailTemplateId: parseInt(e.target.value) || 3,
                      }))
                    }
                    placeholder="Masukkan ID template Brevo"
                  />
                </div>
                <div className="mb-4 text-sm text-gray-600">
                  <p>
                    Email akan dikirim ke{" "}
                    {
                      state.allParticipants.filter(
                        (p) =>
                          state.selectedParticipants.includes(p.id) &&
                          p.transaction_status === "settlement" &&
                          isValidEmail(p.email) &&
                          (!p.sentTemplateIds ||
                            !p.sentTemplateIds.includes(
                              state.emailTemplateId.toString()
                            ))
                      ).length
                    }{" "}
                    peserta dengan status <strong>settlement</strong>, email
                    valid, dan belum menerima template ID{" "}
                    {state.emailTemplateId || 3}.
                  </p>
                </div>
                <div className="flex justify-end gap-4">
                  <Button
                    type="button"
                    onClick={() => setIsEmailModalOpen(false)}
                    variant="secondary"
                    disabled={isSendingEmail}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSendingEmail}
                  >
                    {isSendingEmail ? "Mengirim..." : "Kirim Email"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Upload CSV */}
        {isCsvModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg py-6 px-4 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Unggah BIB Number via CSV
              </h3>
              {csvFileName && (
                <p className="text-sm text-gray-600 mb-2">
                  File: {csvFileName}
                </p>
              )}
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={skipNameValidation}
                    onChange={(e) => setSkipNameValidation(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-yellow-800">
                    Lewati validasi nama depan (hanya cocokkan Order ID)
                  </span>
                </label>
                <p className="text-xs text-yellow-700 mt-1">
                  Gunakan ini jika nama depan di CSV berbeda format dengan
                  database.
                </p>
              </div>
              {csvData.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-2">
                    Pratinjau Data
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-200">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Baris
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Order ID
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Nama Depan
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Nama Belakang
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            BIB Number
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {csvData.map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {index + 2}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Order ID"] || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Nama Depan"] || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Nama Belakang"] || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["BIB Number"] || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {csvErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                  <h4 className="text-md font-semibold text-red-800 mb-2">
                    Kesalahan ({csvErrors.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-red-700">
                    {csvErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {csvSuccessCount > 0 && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700">
                    Berhasil memproses {csvSuccessCount} baris.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-4">
                <Button
                  onClick={() => {
                    setIsCsvModalOpen(false);
                    setCsvData([]);
                    setCsvErrors([]);
                    setCsvSuccessCount(0);
                    setCsvFileName("");
                  }}
                  variant="secondary"
                  disabled={isProcessingCsv}
                >
                  Batal
                </Button>
                <Button
                  onClick={handleProcessCsv}
                  variant="primary"
                  disabled={isProcessingCsv}
                >
                  {isProcessingCsv ? "Memproses..." : "Proses CSV"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
